import { useEffect, useState } from "react";
import { Button, Modal, Spin, Tabs } from "antd";
import { filter, get, isEmpty, map, noop , set , find} from "lodash";
import { Moment } from "moment";

// including local dependencies
import { appInsights, reactPlugin, withAITracking } from "../../../appInsights";
import Intervention, { InterventionPreloader } from "./components/intervention/intervention";
import "./care-coordination.scss";
import { ACTIVE, COMPLETE, NEXT, PREVIOUS } from "./constants";
import { useCareCoordinationStore } from "../../../stores/useCareCoordinationStore";
import Icon from "@assets/icon_config";
import CareCoordinationService from "@services/care-coordination";
import membersService from "@services/members.service";
import Program from "./components/program/program";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { useConfigStore } from "@stores/useConfigStore";
import { CanEdit } from "@components/accessLevel";
import CarePlanReportCustomizationTab from "./components/care-plan-report/CustomisingTab/customisingTab";
import { getClientName, milleniumClientCheck } from "@utils/helpers";
import { useProgramData } from "./customHooks";
/******************************************************************** */

const CareCoordination = () => {
  const { TabPane } = Tabs;

  const programsData = useCareCoordinationStore((state) => state.dataForCareCoordination);
  const setProgramsData = useCareCoordinationStore((state) => state.setDataForCareCoordination);
  const setPreviousDataForCareCoordination = useCareCoordinationStore((state) => state.setPreviousDataForCareCoordination);
  const setRules = useCareCoordinationStore((state) => state.setRules);
  const setValidations = useCareCoordinationStore((state) => state.setValidations);
  const assignedPrograms = useCareCoordinationStore((state) => state.assignedPrograms);
  const setAssignedPrograms = useCareCoordinationStore((state) => state.setAssignedPrograms);
  const setInterventionDetailCollapse = useCareCoordinationStore((state) => state.setInterventionDetailCollapse);
  const [programStartDate, setProgramStartDate] = useState<string | undefined>();

  // local states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedValuesOfProgram, setSelectedValuesOfProgram] = useState<number | undefined>();
  const interventionStartDate = useCareCoordinationStore((state) => state.interventionStartDate);
  const setInterventionStartDate = useCareCoordinationStore((state) => state.setInterventionStartDate);
  const setCareProgramAssigneeList = useCareCoordinationStore((state) => state.setCareProgramAssigneeList);
  const carePlanProgramActive = useCareCoordinationStore((state) => state.carePlanProgramActive);
  const setCarePlanProgramActive = useCareCoordinationStore((state) => state.setCarePlanProgramActive);
  const activeProgram = useCareCoordinationStore((state) => state.activeProgram);
  const setActiveProgram = useCareCoordinationStore((state) => state.setActiveProgram);
  const [programEndDate, setProgramEndDate] = useState<Moment | undefined>();
  const [patientId, setPatientId] = useState<any>(null);

  // states used for Load
  const [loading, setLoading] = useState(false);
  const [showInitialLoadWithShimmer, setShowInitialLoadWithShimmer] = useState<boolean>(true);
  const [programSpecificLoadingWithShimmer, setProgramSpecificLoadingWithShimmer] = useState<boolean>(true);
  const [showInitialLoadWithSpinner, setShowInitialLoadWithSpinner] = useState<boolean>(false);
  const getCurrentMember = membersService?.getCurrentMember;
  const activeKeyForPhysicianCarePlan = useCareCoordinationStore((state) => state.activeKeyForPhysicianCarePlan);
  const setActiveKeyForPhysicianCarePlan = useCareCoordinationStore((state) => state.setActiveKeyForPhysicianCarePlan);

  /*********************************************** */
  const initialLoad = ({ programId, patientProgramId, startDate, startDateUnix, instanceNeeded, patient_ID = patientId , callback}: any) => {
    // fetching info for a specific program
    CareCoordinationService.fetchProgramDetails({
      programId: programId,
      patientProgramId: patientProgramId,
      patientId: patient_ID,
      startDate: programStartDate,
      instanceNeeded: instanceNeeded,
      startDateUnix
    }).subscribe({
      next: (v) => {
        const response = v.data;

        // if we are calling previous or next instance and response is empty then show No data found
        if (instanceNeeded === PREVIOUS && isEmpty(get(response, "data.program", []))) {
          info(PREVIOUS);
        } else if (instanceNeeded === NEXT && isEmpty(get(response, "data.program", []))) {
          info(NEXT);
        } else {
          if (
            isEmpty(get(response, "data.program", {})) &&
            isEmpty(
              filter(get(response, "data.assignedPrograms", []), (program) => {
                return program?.status === COMPLETE || program?.status === ACTIVE;
              })
            )
          ) {
            setActiveProgram(get(response, "data.assignedPrograms", [])?.[0]?.id);
            setInterventionStartDate(get(response, "data.program.startDate", undefined));
          } else {
            setActiveProgram(get(get(response, "data.program", {}), "id", activeProgram));
            setInterventionStartDate(get(response, "data.program.startDate", undefined));
          }
          setRules(get(response, "data.rules", []));
          setValidations(get(response, "data.validations", []));
          if(!callback) {
            setProgramsData(get(response, "data.program", []));
          }
          setPreviousDataForCareCoordination(get(response, "data.program", []));
          setAssignedPrograms(get(response, "data.assignedPrograms", []));         
          setProgramStartDate(get(response, "data.program.startDate", undefined));
        }

        // stopping all the loaders over here
        setShowInitialLoadWithShimmer(false);
        setProgramSpecificLoadingWithShimmer(false);
        setShowInitialLoadWithSpinner(false);
        if(callback) {
          callback({latestProgramData : get(response, "data.program", [])}).then((updatedProgramData : any) => {
            setProgramsData(updatedProgramData);
          })
        }
        carePlanProgramActive ? setActiveProgram("care-plan-config") : noop();
        setCarePlanProgramActive(false);
      },
      error: (e) => {
        console.log(e);
        setShowInitialLoadWithShimmer(false);
        setProgramSpecificLoadingWithShimmer(false);
        setShowInitialLoadWithSpinner(false);
      },
    });
  };

  useEffect(() => {
    console.log("CareCoordination.useEffect");
    appInsights.trackPageView({ name: "Care Coordination" });



    const cMember = getCurrentMember;
    setShowInitialLoadWithShimmer(true);
    setPatientId(parseInt(cMember?.patient_id));
    initialLoad({ patient_ID: parseInt(cMember?.patient_id) });
  }, [getCurrentMember?.patient_id]);

  useEffect(() => {
    if(patientId && milleniumClientCheck()){
    CareCoordinationService.fetchReassignmentAssigneeList(patientId).subscribe({
      next: (v) => {
        setCareProgramAssigneeList(v?.data?.data)
      },
      error: (e) => {
        console.log("fetching assignee list failed")
      },
    });
  }
  },[patientId])
  useEffect(() => {
    carePlanProgramActive ? setActiveProgram("care-plan-config") : noop();
    setCarePlanProgramActive(false);
  }, [carePlanProgramActive]);

  /**
   * @param key function responsible for changing the program tab
   */
  const onChangeProgram = (key: any) => {
    const program = assignedPrograms.find((program: any) => program.id === +key);
    const _patientProgramId = Number(program?.patientProgramId) || null;
    setActiveProgram(key);
    setInterventionDetailCollapse([]);
    if (program?.status) {
      setProgramSpecificLoadingWithShimmer(true);
      initialLoad({ programId: key, patientProgramId: _patientProgramId, startDate: programStartDate });
    }
  };

  /**
   * function responsible for opening pop up for adding new program
   */
  const onEdit = () => {
    setSelectedValuesOfProgram(undefined);
    setInterventionStartDate(undefined);
    setProgramEndDate(undefined);
    setIsModalVisible(true);
  };

  const getProgramStatusColor = (programStatus: string | null) => {
    return programStatus === "Active" ? "#F2960D" : programStatus === "Complete" ? "#1BBE75" : "#B3BAC5";
  };

  const [canEdit] = useConfigStore((store) => [store.canEdit]);

  return (
    <>
      {showInitialLoadWithShimmer ? (
        <InterventionPreloader />
      ) : assignedPrograms.length > 0 ? (
        <Spin spinning={showInitialLoadWithSpinner} indicator={<HealthemSpinner size={70} />}>
          <Tabs
            hideAdd={!canEdit()}
            className="ui-care-coordination__program-tabs"
            activeKey={activeProgram + ""}
            onChange={onChangeProgram}
            type="editable-card"
            onEdit={onEdit}
            tabBarExtraContent={{
              left: activeProgram !== "care-plan-config" ? (
                  <div
                    onClick={() => {
                      onChangeProgram("care-plan-config");
                      setActiveKeyForPhysicianCarePlan(getClientName() !== "Mycare" ? activeKeyForPhysicianCarePlan : ["2"]);
                    }}
                    className="ui-care-coordination__program-tabs__customisingTab"
                  >
                    <Icon name="dashboard" colorOfBrand="#EF6D2A" />
                  </div>
                ) : (
                  <></>
                ),
            }}
          >
            {activeProgram === "care-plan-config" ? (
              <TabPane tab={<Icon name="dashboard" colorOfBrand="#EF6D2A" />} key={"care-plan-config"} className="ui-program" active={activeProgram + "" === "care-plan-config"}>
                <CarePlanReportCustomizationTab assignedPrograms={assignedPrograms} activeProgram={activeProgram} />
              </TabPane>
            ) : (
              <></>
            )}
            {map(assignedPrograms, (program) => {
              const programId = get(program, "id", null);
              const programValue = get(program, "name");
              const programDescription = get(program, "description");
              const programStatus = get(program, "status", ACTIVE);
              const prgmGrpId = get(program, "prgmGrpId", null);
              const prgmMetadata = get(program, "prgmMetadata", []); 
              return (
                <TabPane
                  tab={
                    <div className="ui-program-name">
                      <span className="program-status">
                        <Icon name="program-status" colorOfBrand={getProgramStatusColor(programStatus)} />
                      </span>
                      {/* need to show the full forms instead of abbreviation */}
                      {programDescription}
                    </div>
                  }
                  key={programId}
                  className="ui-program"
                  active={activeProgram + "" === programId + ""}
                >
                  {programSpecificLoadingWithShimmer ? (
                    <InterventionPreloader />
                  ) : (
                    activeProgram === programId && (
                      <Intervention
                        programName={programValue}
                        programId={programId}
                        patientId={patientId}
                        programStatus={programStatus}
                        initialLoad={initialLoad}
                        setProgramLoading={setShowInitialLoadWithSpinner}
                        onChangeProgram={onChangeProgram}
                        interventionStartDate={interventionStartDate}
                        prgmGrpId={prgmGrpId}
                        prgmMetadata={prgmMetadata}
                      />
                    )
                  )}
                </TabPane>
              );
            })}
          </Tabs>
        </Spin>
      ) : (
        <div className="add_Program_Icon__container">
          <Icon name="addProgram" />
          <span>No Programs here yet!</span>
          <CanEdit>
            <Spin spinning={loading} indicator={<HealthemSpinner />}>
              <Button type="primary" size="large" className="f16 w500" onClick={() => onEdit()}>
                Add New Program
              </Button>
            </Spin>
          </CanEdit>
        </div>
      )}

      <Program
        patientId={patientId}
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        selectedValuesOfProgram={selectedValuesOfProgram}
        setSelectedValuesOfProgram={setSelectedValuesOfProgram}
        programEndDate={programEndDate}
        setProgramEndDate={setProgramEndDate}
        initialLoad={initialLoad}
        loading={loading}
        setLoading={setLoading}
        programStartDate={programStartDate}
        setProgramLoading={setProgramSpecificLoadingWithShimmer}
      />
    </>
  );
};

const info = (flag: {} | null | undefined) => {
  Modal.info({
    title: "Information",
    content: (
      <div>
        <p>
          This program does not have any <b>{flag}</b> instance.
        </p>
      </div>
    ),
    onOk() {},
  });
};

export default withAITracking(reactPlugin, CareCoordination);
